import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  FileText,
  Wallet,
  Building2,
  BarChart3,
  LogOut,
  Menu,
  X,
  User,
  Landmark,
  CreditCard,
  Calculator,
  CalendarClock,
  ClipboardList,
  Users,
  Home,
  Receipt,
  Package,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Map paths to permission keys
const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, permKey: "dashboard" },
  { path: "/fatture", label: "Fatture", icon: FileText, permKey: "fatture" },
  { path: "/movimenti", label: "Movimenti Bancari", icon: Landmark, permKey: "movimenti" },
  { path: "/conti", label: "Conti", icon: Wallet, permKey: "conti" },
  { path: "/centri-costo", label: "Centri di Costo", icon: Building2, permKey: "centri_costo" },
  { path: "/modalita-pagamento", label: "Mod. Pagamento", icon: CreditCard, permKey: "modalita_pagamento" },
  { path: "/scadenze-ricorrenti", label: "Scadenze Ricorrenti", icon: CalendarClock, permKey: "scadenzario" },
  { path: "/appartamenti", label: "Appartamenti", icon: Home, permKey: "appartamenti" },
  { path: "/magazzino", label: "Magazzino", icon: Package, permKey: "dashboard" },
  { path: "/bilancio-competenza", label: "Bilancio", icon: Calculator, permKey: "bilancio" },
  { path: "/scadenziario", label: "Scadenziario", icon: ClipboardList, permKey: "report_scadenze" },
  { path: "/report-affitti", label: "Report Affitti", icon: Receipt, permKey: "report_scadenze" },
  { path: "/report", label: "Altri Report", icon: BarChart3, permKey: "report" },
  { path: "/gestione-utenti", label: "Gestione Utenti", icon: Users, permKey: "gestione_utenti" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filter nav items based on user permissions
  const userPermissions = user?.permissions || {};
  const filteredNavItems = navItems.filter(item => {
    const perm = userPermissions[item.permKey];
    return perm?.view === true;
  });

  return (
    <div className="min-h-screen bg-background" data-testid="main-layout">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="font-bold text-lg">Fatturazione</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="user-menu-mobile">
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={logout} data-testid="logout-btn-mobile">
              <LogOut className="mr-2 h-4 w-4" /> Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r z-40 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:block`}
        data-testid="sidebar"
      >
        <div className="p-6">
          <h1 className="text-xl font-bold text-primary">TEN03</h1>
          <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">Gestione Contabile</p>
        </div>

        <nav className="px-3 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">
                {user?.nome?.[0]}{user?.cognome?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.nome} {user?.cognome}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
              className="text-muted-foreground hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
